import { Box, Grid } from '@material-ui/core';
import { ArticleType, Currency, getMoneySymbolByCurrency } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';

import { DescriptionFieldTooltip } from '@/components/modules/Application/components/Cart/DefaultCart/components/CartForm/components/CartItem/components/DescriptionFieldTooltip';
import { CartItemActions } from '@/components/modules/Application/components/Cart/DefaultCart/components/CartForm/components/CartItemActions/CartItemActions';
import { useTableLayoutStyle } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useTableLayoutStyle';
import { CartFormActions } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/CartFormActions';
import { MinimalFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/MinimalFormItem';
import { getTotalPrice } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/utils/getTotalPrice';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useScreen } from '@/hooks/useScreen';

export interface CartItemProps {
  index: number;
  items: MinimalFormItem[];
  cartFormActions: CartFormActions;
  currency: Currency;
}

const DISABLED_DESCRIPTION_ARTICLE_TYPES = [ArticleType.FLIGHT, ArticleType.AUTOMOBILE, ArticleType.ACCOMMODATION];

export const CartItem = ({ index, items, cartFormActions, currency }: CartItemProps) => {
  const { sm, md } = useScreen();
  const { getItemSizes, tableSpacing } = useTableLayoutStyle();
  const { register, watch } = useFormContext();

  const currentItem = watch(`items.${index}`);
  const currencySymbol = getMoneySymbolByCurrency(currency);

  if (!currentItem) return null;

  return (
    <CustomPaper
      fill={!sm ? 'paper' : 'inherit'}
      style={{
        ...(sm && { padding: 0 }),
        marginBottom: md ? 0 : 24,
        boxShadow: 'none',
      }}
    >
      <Box display={'flex'} flexDirection={sm ? 'row' : 'column'}>
        <Grid container style={{ position: 'relative', flexGrow: 1 }} spacing={tableSpacing}>
          <Grid item xs={2} sm={getItemSizes(0)} style={{ margin: '0!important' }}>
            <NumberFormField
              type={'number'}
              name={`items.${index}.units`}
              disabled={currentItem.type !== ArticleType.ONLINE_PRODUCT}
              label={!sm ? 'UNITS_SHORT' : undefined}
              InputProps={{ inputProps: { min: 1 } }}
              style={{ textAlign: sm ? 'center' : 'left' }}
              fieldVariant={FieldVariant.form}
              allowDecimals={false}
            />
          </Grid>
          <Grid item xs={10} sm={getItemSizes(1)}>
            <TextFormField
              autoFocus={index > 0}
              name={`items.${index}.description`}
              label={!sm ? 'DESCRIPTION' : undefined}
              disabled={DISABLED_DESCRIPTION_ARTICLE_TYPES.includes(currentItem.type)}
              InputProps={{
                startAdornment: <DescriptionFieldTooltip type={currentItem.type} category={currentItem.category} />,
                inputProps: { style: { minWidth: 'calc(100% - 26px)' } },
              }}
              fieldVariant={FieldVariant.form}
            />
          </Grid>
          <Grid item xs={4} sm={getItemSizes(2)}>
            <NumberFormField
              name={`items.${index}.price`}
              valueUnit={currencySymbol}
              label={!sm ? 'PRICE' : undefined}
              fieldVariant={FieldVariant.form}
              style={{ textAlign: 'right' }}
            />
          </Grid>
          <Grid item xs={4} sm={getItemSizes(3)}>
            <NumberFormField
              name={''}
              dummyField
              valueUnit={currencySymbol}
              label={!sm ? 'AMOUNT' : undefined} //fixme
              value={getTotalPrice(currentItem)}
              fieldVariant={FieldVariant.form}
              forceSingleLine
              style={{ textAlign: 'right' }}
            />
            <input type="hidden" {...register(`items.${index}.category`)} />
            <input type="hidden" {...register(`items.${index}.type`)} />
          </Grid>
        </Grid>
        <CartItemActions index={index} items={items} cartFormActions={cartFormActions} currency={currency} />
      </Box>
    </CustomPaper>
  );
};
